import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TabBar from '../components/TabBar';
import { Flame, BookOpen, Pen, XCircle, BarChart3, Clock, FileText, MessageSquare, Calendar, ChevronRight, Award, User, Phone, Crown } from 'lucide-react';
import { getCurrentUser, checkUserSession } from '../utils/profilesAPI';
import { getLearningStats } from '../utils/subjectHistorysAPI';
import { getAllExams, getExamsInfoBatch, calculateTotalQuestions, calculateTotalDuration } from '../utils/examAPI';

function Home({ session }) {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exams, setExams] = useState([]);
  const [examTotals, setExamTotals] = useState({});
  const [examDurations, setExamDurations] = useState({});
  const [learningDays, setLearningDays] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [averageScore, setAverageScore] = useState(0);
  const [overallAccuracy, setOverallAccuracy] = useState(0);
  const navigate = useNavigate();

  /**
   * 检查用户登录状态，如果未登录则重定向到登录页
   */
  useEffect(() => {
    const checkAuth = async () => {
      // 首先检查本地存储中的登录状态，避免无谓的API调用
      const userId = localStorage.getItem('user_id');
      const isExperienceMode = localStorage.getItem('experience_mode') === 'true';
      
      // 如果本地已有登录信息，则不再进行API调用
      if (userId || isExperienceMode || session) {
        return;
      }

      try {
        // 只有在本地没有登录信息时，才进行API调用检查登录状态
        const isLoggedIn = await checkUserSession(session);
        
        // 如果未登录，重定向到登录页
        if (!isLoggedIn) {
          console.log('用户未登录，重定向到登录页');
          navigate('/login');
        }
      } catch (error) {
        console.error('检查登录状态出错:', error.message);
        // 发生错误时也重定向到登录页
        navigate('/login');
      }
    };
    
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 由于useEffect依赖项被移除，添加eslint注释禁用警告

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        
        // 检查是否处于体验模式
        const isExperienceMode = localStorage.getItem('experience_mode') === 'true';
        
        // 使用profilesAPI的getCurrentUser函数获取用户资料
        const profile = await getCurrentUser(session);
        
        if (profile) {
          // 确保体验用户标记
          if (isExperienceMode && !profile.experienceUser) {
            profile.experienceUser = true;
          }
          
          setUserProfile(profile);
          
          // 获取考试列表
          const examsData = await getAllExams();
          setExams(examsData || []);
          
          // 如果有考试数据，在单独的useEffect中处理考试信息
          if (examsData && examsData.length > 0) {
            // 批量获取考试信息（题目数和时长）
            try {
              const { totals, durations } = await getExamsInfoBatch(examsData);
              setExamTotals(totals);
              setExamDurations(durations);
            } catch (error) {
              console.error('获取考试信息失败:', error);
            }
          }
        } else {
          console.error('用户资料不存在');
        }
      } catch (error) {
        console.error('获取用户资料出错:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [session]);

  // 单独处理学习统计数据，避免多重依赖引发的重复渲染
  useEffect(() => {
    const fetchLearningStats = async () => {
      if (!userProfile) return;
      
      // 确定用户ID，优先使用id，然后是_openid，最后是openid
      const userId = userProfile.id || userProfile._openid || userProfile.openid || userProfile._id;
      
      if (userId) {
        // 使用subjectHistorysAPI的getLearningStats函数获取学习统计数据
        try {
          console.log('获取学习统计数据, userId:', userId);
          const stats = await getLearningStats(userId);
          
          // 如果是新体验用户且没有学习记录，保持初始状态
          if (userProfile.experienceUser && stats.recordsCount === 0) {
            setLearningDays(0);
            setTotalQuestions(0);
            setAverageScore(0);
            setOverallAccuracy(0);
          } else {
            setLearningDays(stats.learningDays);
            setTotalQuestions(stats.totalQuestions);
            setAverageScore(stats.averageScore);
            setOverallAccuracy(stats.overallAccuracy);
          }
        } catch (statsError) {
          console.error('获取学习统计数据出错:', statsError);
          // 设置默认值
          setLearningDays(0);
          setTotalQuestions(0);
          setAverageScore(0);
          setOverallAccuracy(0);
        }
      }
    };
    
    fetchLearningStats();
  }, [userProfile]);

  // 单独处理后台计算额外的考试数据，避免与主渲染循环冲突
  useEffect(() => {
    if (!exams || exams.length === 0) return;
    
    // 计算额外考试数据的函数
    const calculateExtraExamData = async () => {
      const updatedTotals = {...examTotals};
      const updatedDurations = {...examDurations};
      let hasUpdates = false;
      
      for (const exam of exams) {
        if (!updatedTotals[exam._id]) {
          try {
            const total = await calculateTotalQuestions(exam._id);
            if (total) {
              updatedTotals[exam._id] = total;
              hasUpdates = true;
            }
          } catch (error) {
            console.error(`获取考试 ${exam._id} 题目数出错:`, error);
          }
        }
        
        if (!updatedDurations[exam._id]) {
          try {
            const duration = await calculateTotalDuration(exam._id);
            if (duration) {
              updatedDurations[exam._id] = duration;
              hasUpdates = true;
            }
          } catch (error) {
            console.error(`获取考试 ${exam._id} 时长出错:`, error);
          }
        }
      }
      
      if (hasUpdates) {
        setExamTotals(prev => ({...prev, ...updatedTotals}));
        setExamDurations(prev => ({...prev, ...updatedDurations}));
      }
    };
    
    // 使用setTimeout避免阻塞主线程渲染
    const timerId = setTimeout(calculateExtraExamData, 500);
    
    // 清理函数
    return () => clearTimeout(timerId);
  }, [exams]); // 只依赖exams，移除examTotals和examDurations依赖，避免循环渲染

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-bg-color pb-20">
      {/* 顶部用户信息 */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white p-5 pb-16 shadow-md">
        <div className="flex items-center">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-2 border-white/40">
            {userProfile?.experienceUser ? (
              <span className="text-2xl font-bold">体</span>
            ) : userProfile?.nickname ? (
              <span className="text-2xl font-bold">
                {userProfile.nickname.charAt(0).toUpperCase()}
              </span>
            ) : (
              <span className="text-2xl font-bold">同</span>
            )}
          </div>
          <div className="ml-4">
            <h1 className="text-xl font-bold">
              {userProfile?.experienceUser ? '体验用户' : (userProfile?.nickname || '同学')}
              {userProfile?.experienceUser && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                  体验模式
                </span>
              )}
            </h1>
            <div className="flex items-center text-sm mt-1">
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-1" />
                <span>{userProfile?.experienceUser ? '体验账号' : (userProfile?.phone ? `${userProfile.phone.substring(0, 3)}****${userProfile.phone.substring(7)}` : '未绑定手机')}</span>
              </div>
              {userProfile?.isVip || userProfile?.experienceUser ? (
                <div className="flex items-center text-sm ml-4">
                  <Crown className="w-4 h-4 mr-1 text-yellow-300" />
                  <span className="text-yellow-100">VIP会员{userProfile?.experienceUser ? '(体验)' : ''}</span>
                </div>
              ) : (
                <div 
                  className="flex items-center text-sm ml-4 cursor-pointer" 
                  onClick={() => navigate('/vip')}
                >
                  <Crown className="w-4 h-4 mr-1 text-gray-300" />
                  <span className="text-gray-200">开通VIP会员</span>
                  <ChevronRight className="w-3 h-3 ml-1 text-gray-300" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 功能卡片区域 */}
      <div className="px-4 mt-5 margin-top-50">
        <div className="bg-white rounded-lg p-5 shadow-sm">
          <div className="grid grid-cols-4 gap-4">
            <Link to="/question-bank" className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-lg bg-blue-500 flex items-center justify-center mb-1 shadow-sm">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <span className="text-sm text-gray-800">题库练习</span>
            </Link>
            <Link to="/exam" className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-lg bg-green-500 flex items-center justify-center mb-1 shadow-sm">
                <Pen className="w-7 h-7 text-white" />
              </div>
              <span className="text-sm text-gray-800">模拟考试</span>
            </Link>
            <Link to="/mistakes" className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-lg bg-red-500 flex items-center justify-center mb-1 shadow-sm">
                <XCircle className="w-7 h-7 text-white" />
              </div>
              <span className="text-sm text-gray-800">错题本</span>
            </Link>
            <Link to="/study-report" className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-lg bg-orange-500 flex items-center justify-center mb-1 shadow-sm">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <span className="text-sm text-gray-800">学习报告</span>
            </Link>
            <Link to="/messages" className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-lg bg-blue-500 flex items-center justify-center mb-1 shadow-sm">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <span className="text-sm text-gray-800">消息</span>
            </Link>
            <Link to="/achievements" className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-lg bg-blue-500 flex items-center justify-center mb-1 shadow-sm">
                <Award className="w-7 h-7 text-white" />
              </div>
              <span className="text-sm text-gray-800">成就</span>
            </Link>
            <Link to="/profile" className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-lg bg-blue-500 flex items-center justify-center mb-1 shadow-sm">
                <User className="w-7 h-7 text-white" />
              </div>
              <span className="text-sm text-gray-800">个人中心</span>
            </Link>
            
          </div>
        </div>
      </div>

      {/* 推荐考试区域 */}
      <div className="px-4 pt-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-bold text-gray-800 flex items-center">
            <Flame className="w-5 h-5 text-blue-600 mr-2" />
            推荐考试
          </h2>
          <Link to="/exam" className="flex items-center text-xs text-gray-500">
            更多 <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="space-y-4">
          {exams && exams.length > 0 ? exams.slice(0, 2).map((exam, index) => (
            <div key={exam._id || `exam-${index}`} className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-800">{exam.name}</h3>
                <span className="px-3 py-1 text-xs bg-blue-100 text-blue-600 rounded-full">
                  推荐
                </span>
              </div>
              
              <div className="flex text-sm text-gray-500 mb-3 space-x-4">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{examDurations[exam._id] || exam.duration || '未知'}分钟</span>
                </div>
                <div className="flex items-center">
                  <FileText className="w-4 h-4 mr-1" />
                  <span>{examTotals[exam._id] || exam.count || '未知'}题</span>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Link
                  to={`/exam-start/${exam._id}`}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-full text-sm shadow-sm hover:shadow transition-all"
                >
                  开始考试
                </Link>
              </div>
            </div>
          )) : (
            <div className="bg-white rounded-lg p-8 shadow-sm text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500">暂无考试数据</p>
              <button 
                className="mt-4 px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm"
                onClick={() => navigate('/exam')}
              >
                浏览考试列表
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 学习进度区域 */}
      <div className="px-4 pt-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-bold text-gray-800 flex items-center">
            <BarChart3 className="w-5 h-5 text-secondary mr-2" />
            学习进度
          </h2>
          <Link to="/study-report" className="flex items-center text-xs text-gray-500">
            详情 <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-gray-800">学习数据统计</h3>
              <p className="text-sm text-gray-500 mt-1">总学习天数: {learningDays} 天</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-secondary-light/20 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-secondary" />
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">累计答题数</span>
                <span className="text-gray-500">{totalQuestions}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="bg-green-500 h-full rounded-full" style={{ width: `${Math.min(100, totalQuestions > 1000 ? 100 : totalQuestions/10)}%` }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">平均分</span>
                <span className="text-gray-500">{averageScore}/100</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="bg-blue-500 h-full rounded-full" style={{ width: `${averageScore}%` }}></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">总体正确率</span>
                <span className="text-gray-500">{overallAccuracy}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="bg-secondary h-full rounded-full" style={{ width: `${overallAccuracy}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TabBar />
    </div>
  );
}

export default Home; 